<template>
  <div class="pdp-wrapper large-margin-top-xs">
    <main class="content grid-container">
      <NuxtPage />
    </main>
    <ProviderWrapper v-if="isSlideoutAvailable" v-bind="slideout.providerProps">
      <Slideout
        v-bind="slideout.rootProps"
        :id="slideout.id"
        :class="slideout.class"
        :title="slideout.title"
        :back-config="{ backable: slideout.backable, footerText: '' }"
        :position="slideout.position"
        @back-slideout="
          () => {
            slideout?.onBack?.();
            backSlideout();
          }
        "
        @close-slideout="
          () => {
            slideout?.onClose?.();
            closeSlideout();
          }
        "
      >
        <component :is="slideout.component" v-if="slideout.component" v-bind="slideout.props" v-on="slideout?.evtHandlers ?? {}" />
        <template v-if="!!slideout.footerComponent" #footer>
          <component :is="slideout.footerComponent" v-bind="slideout.footerProps" v-on="slideout?.footerEvtHandlers ?? {}" />
        </template>
      </Slideout>
    </ProviderWrapper>

    <Soasta />
  </div>
</template>
<script setup lang="ts">
import { onBeforeMount, markRaw } from 'vue';
import useProductStore from '~/stores/useProductStore';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import usePageData from '~/stores/usePageData';
import useExperiments from '~/stores/useExperimentsStore';
import { useSlideout } from '~/composables/utils/useSlideout';
import Slideout from '@atomic-ui/slideout/Slideout';
import useSiteMonGAMTags from '~/composables/useSitemonGAMTags';
import useRoktSDKPlus from '@nuxt-common/site-monetization/app/composables/useRoktSDKPlus';
import onHeader from '~/composables/onHeader';
import { useBrand } from '~/composables/useBrand';
import { useDeviceTypeProvide } from '~/composables/utils/useDeviceType';
import useATRAnalytics from '~/composables/useATRAnalytics';
import Soasta from '~/components/SoastaView';
import isUpcsAvaiable from './utils/isMemberProductAvailable';
import { getMemberProductMap, getMemberProducts } from '~/utils/product';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import ProviderWrapper from './components/common/ProviderWrapper.vue';

const { killswitches } = useKillswitches();

const { isRoktSDKPlusEnabled } = killswitches;

const productStore = useProductStore();
const consolidatedMemberStore = useConsolidatedMemberStore();
const route = useRoute();

// TODO separate out pageData and product logic so they independent, probably can deprecate pageData
const { data: pageData, error: pageDataError } = await useAsyncData(
  'pageData',
  async () =>
    await $fetch('/api/product', {
      headers: useRequestHeaders(),
      params: route.query,
    }),
);

// Return correct status code for pages with error
if (import.meta.server) {
  const event = useRequestEvent();

  if (event) {
    if (pageDataError.value) {
      setResponseStatus(event, pageDataError.value.statusCode, pageDataError.value.statusMessage);
    }
  }
}

usePageData().pageData = pageDataError.value ? {} : markRaw(pageData.value);
const product = pageData.value?.product?.product || {};
productStore.product = pageDataError.value ? null : markRaw(product);

if (!pageDataError.value && pageData.value?.flags?.groupByProducts?.[product?.id]?.featureEligibilityFlags?.isShopTheSetExpEnabled) {
  const selectedProduct: string = product?.relationships?.selectedProduct;
  const productMap = getMemberProductMap(product);

  if (isUpcsAvaiable(productMap[selectedProduct])) {
    consolidatedMemberStore.selectedMember = +selectedProduct;
  } else {
    const productsMap = getMemberProducts(product);
    const firstAvailable = productsMap.find(isUpcsAvaiable);
    const firstAvailableId = firstAvailable?.id;
    consolidatedMemberStore.selectedMember = firstAvailableId ? +firstAvailableId : null;
  }
}

const { data: envProps } = await useAsyncData('envProps', async () => await $fetch('/api/envProps'));
useEnvProps().envProps = envProps.value;

useDeviceTypeProvide();
useExperiments().init(pageData.value?.experimentFlags, pageData.value?.experimentIds);
onBeforeMount(() => {
  // TODO - remove this later, resolves temporary analytics bug (remove 'onBeforeMount' and 'useBrand')
  const { isBcom } = useBrand();
  const productData = productStore.product;
  if (!productData) return;
  window.__PDP_NUXT_DATA__ = {
    traits: productData?.traits,
    imagery: productData?.imagery,
    ...(isBcom && { relationships: productData?.relationships }),
  };
});
onMounted(() => {
  const { isBcom } = useBrand();
  const page = 'pdp';
  useSiteMonGAMTags();

  useRoktSDKPlus({
    brand: isBcom ? 'bcom' : 'mcom',
    isRoktSDKPlusEnabled,
    screenName: page,
  });
});
onHeader();

const { slideout, isSlideoutAvailable, backSlideout, clearSlideoutHistory } = useSlideout();
const { fireAnalyticsOnATRClose } = useATRAnalytics();

const closeSlideout = () => {
  fireAnalyticsOnATRClose();
  // call last - nulls slideout computedRef value
  clearSlideoutHistory();
};
</script>

<style lang="scss">
@import '~appSCSS';

.pdp-wrapper {
  display: flex;
  flex-direction: column;
}

.content {
  flex-grow: 1;
}

.header,
.content,
.footer {
  width: 100%;
}

#product-details-drawer {
  @include breakpoint(small only) {
    height: 100%;
    max-height: 100%;
  }
}
</style>



<style lang="scss" brand="mcom">
#header.mcom-mobile-hidden-header {
  @include breakpoint(small only) {
    display: none;
  }
}

#similar-items-slideout {
  @include breakpoint(small only) {
    height: rem-calc(480);
  }
}
</style>
